// Mappers for employee contract list operations
import type {
  LC_GetEmployeeContractsOut,
  LC_GetEmployeeContractsOutLC_PagedResult,
  EmployeeContract,
  EmployeeContractsPage,
  ContractStatus,
  ContractType,
} from '../types/employeeContractList.types';


// Convert ISO date-time to dd/mm/yyyy format
const formatDateFromISO = (isoDate: string | null): string | null => {
  if (!isoDate) return null;
  
  try {
    const date = new Date(isoDate);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return null;
  }
};

// Map status from API to internal model
export const mapContractStatus = (status: string | null): ContractStatus => {
  if (!status) return 'active';
  
  const normalized = status.trim().toLowerCase();
  switch (normalized) {
    case 'activo':
      return 'active';
    case 'terminado':
      return 'terminated';
    default:
      return 'active';
  }
};

// Map contract type from API to internal model
export const mapContractType = (type: string | null): ContractType => {
  if (!type) return 'indefinite';
  
  const normalized = type.trim().toLowerCase();
  switch (normalized) {
    case 'indefinido':
      return 'indefinite';
    case 'plazo fijo':
      return 'fixed_term';
    case 'honorarios':
      return 'fees';
    case 'por obra':
      return 'per_project';
    default:
      return 'indefinite';
  }
};

// Map single contract DTO to internal model
export const mapContractOutToModel = (dto: LC_GetEmployeeContractsOut): EmployeeContract => ({
  contractId: dto.contractId,
  contractNumber: dto.contractNumberREX,
  contractName: dto.contractNameREX || '',
  status: mapContractStatus(dto.contractStatus),
  startDate: formatDateFromISO(dto.contractStartDate) || '',
  endDate: formatDateFromISO(dto.contractEndDate),
  companyName: dto.companyName || '',
  contractType: mapContractType(dto.contractType),
});

// Map paged response to internal model
export const mapPagedContracts = (
  out: LC_GetEmployeeContractsOutLC_PagedResult,
): EmployeeContractsPage => {
  const pagination = out.pagination;
  const data = (out.data || []).map(mapContractOutToModel);

  return {
    data,
    page: pagination.currentPage,
    pageSize: pagination.pageSize,
    totalRecords: pagination.totalRecords,
    totalPages: pagination.totalPages,
    hasNextPage: pagination.hasNextPage,
    hasPreviousPage: pagination.hasPreviousPage,
  };
};
